import type { ReactElement } from "react";
import { Link } from "@tanstack/react-router";

import { MarketingLayout } from "./sections/Layout";
import { Card, CardContent } from "@/components/ui/card";

const TFN_DISPLAY = "(888) 404-9410";

const CONFIRMATION = `Chill: You're confirmed. Reply with edits anytime to update your site (events, photos, hours, music, contact). Msg frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to opt out. Terms: getchill.ai/legal/terms · Privacy: getchill.ai/legal/privacy`;

const HELP_REPLY = `Chill: Text any change to your site (events, photos, hours, music, contact). More: getchill.ai/legal/sms. Reply STOP to opt out. Msg & data rates may apply.`;

const STOP_REPLY = `Chill: You've been unsubscribed. You will receive no further messages. Reply START to resubscribe.`;

export function SmsConsent(): ReactElement {
  return (
    <MarketingLayout>
      <main className="mx-auto w-full max-w-3xl px-6 py-16 sm:py-24">
        <p className="text-sm font-medium uppercase tracking-wider text-primary">
          SMS messaging program
        </p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl">
          How customers opt in to SMS from Chill
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-foreground/80">
          This page describes the SMS messaging program offered by Chill at{" "}
          <Link to="/" className="text-primary hover:underline">
            getchill.ai
          </Link>
          . It covers what messages we send, how account holders affirmatively
          opt in, the exact confirmation and help-reply text, and how to opt
          out. It exists to satisfy carrier verification requirements and to be
          transparent with our customers.
        </p>

        <div className="mt-12 space-y-10 text-base leading-relaxed text-foreground/90">
          <section>
            <h2 className="text-2xl font-semibold tracking-tight">
              Program details
            </h2>
            <dl className="mt-4 grid gap-4 sm:grid-cols-[max-content_1fr]">
              <dt className="font-medium text-foreground">Program name</dt>
              <dd>Chill</dd>
              <dt className="font-medium text-foreground">Operator</dt>
              <dd>
                Chill, operated by Max Giraldo (sole proprietor), Los Angeles,
                California, USA
              </dd>
              <dt className="font-medium text-foreground">Toll-free number</dt>
              <dd>
                <span className="font-mono">{TFN_DISPLAY}</span>
              </dd>
              <dt className="font-medium text-foreground">Purpose</dt>
              <dd>
                Account holders of Chill text our toll-free number to update
                their own website. The Chill AI agent receives the message,
                applies the requested change to the customer's site, and
                replies with a confirmation. All SMS traffic is 1:1 between an
                account holder and their own Chill account. We do not send
                marketing, promotional, third-party, or bulk messages.
              </dd>
              <dt className="font-medium text-foreground">Frequency</dt>
              <dd>
                Conversational. Typically one reply per inbound message a
                customer sends. Frequency varies depending on how often a
                customer chooses to update their site.
              </dd>
              <dt className="font-medium text-foreground">Cost</dt>
              <dd>Standard message and data rates apply.</dd>
              <dt className="font-medium text-foreground">Carrier</dt>
              <dd>SMS is delivered via Twilio.</dd>
            </dl>
          </section>

          <section>
            <h2 className="text-2xl font-semibold tracking-tight">
              How customers opt in
            </h2>
            <p className="mt-4">
              The opt-in is via text — the inbound SMS from a customer's phone
              is the affirmative opt-in. The flow is:
            </p>
            <ol className="mt-4 list-decimal space-y-3 pl-6">
              <li>
                A customer signs up at{" "}
                <Link to="/signup" className="text-primary hover:underline">
                  getchill.ai/signup
                </Link>
                , creates an account, and pays the subscription. (SMS is{" "}
                <strong>not</strong> required to use Chill — customers can
                update their site after signing in instead. SMS is
                offered as one optional channel.)
              </li>
              <li>
                After signing in at{" "}
                <span className="font-mono">getchill.ai/login</span>, the
                customer is shown a unique binding code and our toll-free
                number{" "}
                <span className="font-mono">{TFN_DISPLAY}</span> together with
                the disclosure language reproduced below.
              </li>
              <li>
                The customer texts the binding code from their personal phone
                to our toll-free number. Texting the code is the affirmative
                opt-in.
              </li>
              <li>
                Our system matches the inbound code, binds the phone number to
                the customer's account, and replies with the confirmation
                message reproduced below.
              </li>
              <li>
                We retain a record of each opt-in (timestamp, originating
                phone number, inbound message body, MessageSid) for compliance
                and audit.
              </li>
            </ol>
            <p className="mt-4">
              The disclosure language shown to the customer during phone setup
              reads:
            </p>
            <Card className="mt-4 border-border/60 bg-muted/30">
              <CardContent className="p-6 text-sm leading-relaxed">
                By texting the code above to {TFN_DISPLAY}, you agree to
                receive SMS messages from Chill at the number you text from.
                You will receive transactional confirmations of changes you
                request and direct replies to questions you send. Message
                frequency varies. Message and data rates may apply. Reply HELP
                for help, STOP to opt out. See our{" "}
                <Link
                  to="/legal/$doc"
                  params={{ doc: "terms" }}
                  className="text-primary underline"
                >
                  Terms
                </Link>{" "}
                and{" "}
                <Link
                  to="/legal/$doc"
                  params={{ doc: "privacy" }}
                  className="text-primary underline"
                >
                  Privacy Policy
                </Link>
                .
              </CardContent>
            </Card>
          </section>

          <section>
            <h2 className="text-2xl font-semibold tracking-tight">
              Confirmation message (sent on opt-in)
            </h2>
            <Card className="mt-4">
              <CardContent className="p-6 font-mono text-sm leading-relaxed">
                {CONFIRMATION}
              </CardContent>
            </Card>
          </section>

          <section>
            <h2 className="text-2xl font-semibold tracking-tight">
              Sample exchange
            </h2>
            <Card className="mt-4">
              <CardContent className="space-y-4 p-6 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    Customer → Chill
                  </p>
                  <p className="mt-1 font-mono leading-relaxed">
                    hey can you add saturday may 23 8:30 at smoke house burbank
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    Chill → Customer
                  </p>
                  <p className="mt-1 font-mono leading-relaxed">
                    Got it — added Sat May 23, 8:30 PM @ Smoke House (Burbank).
                    Live now: starbuckjazz.com/events ✓
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          <section>
            <h2 className="text-2xl font-semibold tracking-tight">
              Keywords + opt-out
            </h2>
            <div className="mt-4 space-y-6">
              <div>
                <h3 className="font-semibold">HELP</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Reply if a customer needs assistance:
                </p>
                <Card className="mt-2">
                  <CardContent className="p-4 font-mono text-sm leading-relaxed">
                    {HELP_REPLY}
                  </CardContent>
                </Card>
              </div>
              <div>
                <h3 className="font-semibold">
                  STOP / UNSUBSCRIBE / CANCEL / END / QUIT
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  We immediately remove the number from the program and reply:
                </p>
                <Card className="mt-2">
                  <CardContent className="p-4 font-mono text-sm leading-relaxed">
                    {STOP_REPLY}
                  </CardContent>
                </Card>
              </div>
              <div>
                <h3 className="font-semibold">START / UNSTOP</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Re-subscribes a previously opted-out number; we resend the
                  full confirmation message above.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold tracking-tight">
              Records and retention
            </h2>
            <p className="mt-4">
              We retain a record of each opt-in (timestamp, phone number,
              inbound message body, Twilio MessageSid) for one year for
              compliance and audit. Opt-out events are retained indefinitely
              so we can honor the opt-out across re-binding attempts.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold tracking-tight">Contact</h2>
            <p className="mt-4">
              Questions about this messaging program:{" "}
              <a
                href="mailto:hello@getchill.ai"
                className="text-primary hover:underline"
              >
                hello@getchill.ai
              </a>
              .
            </p>
          </section>

          <p className="border-t border-border/60 pt-8 text-sm text-muted-foreground">
            Last updated: May 7, 2026.
          </p>
        </div>
      </main>
    </MarketingLayout>
  );
}
